// ---------------------------------------------------------------------------
// Tenant Admin module — Config Promotions list page
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { admin } from '@noxverify/api-contracts';
import { api } from '../lib/api';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { useFormatters } from '../hooks/useFormatters';

export function ConfigPromotionsPage() {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const scopeTypeLabels = t('configPromotions.scopeTypes', { returnObjects: true }) as Record<string, string>;
  const kindLabels = t('configPromotions.kinds', { returnObjects: true }) as Record<string, string>;
  const columns = useMemo<Column<admin.ConfigVersionSummary>[]>(
    () => [
      {
        key: 'scopeType',
        header: t('configPromotions.columns.scope'),
        render: (c) => (
          <span className="text-sm text-ink">
            {scopeTypeLabels[c.scopeType] ?? c.scopeType}
          </span>
        ),
      },
      {
        key: 'kind',
        header: t('configPromotions.columns.kind'),
        render: (c) => (
          <span className="text-sm text-ink-muted">
            {kindLabels[c.kind] ?? c.kind}
          </span>
        ),
      },
      {
        key: 'versionNumber',
        header: t('configPromotions.columns.version'),
        render: (c) => (
          <span className="text-sm font-mono text-ink">
            v{c.versionNumber}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('configPromotions.columns.status'),
        render: (c) => <StatusBadge value={c.status} />,
      },
      {
        key: 'scopeId',
        header: t('configPromotions.columns.scopeId'),
        render: (c) => (
          <span className="text-sm font-mono text-ink-muted">{c.scopeId}</span>
        ),
      },
      {
        key: 'createdAt',
        header: t('configPromotions.columns.created'),
        render: (c) => (
          <span className="text-sm text-ink-muted tabular-nums font-mono">
            {f.date(c.createdAt, { dateStyle: 'medium' })}
          </span>
        ),
      },
    ],
    [f, t, scopeTypeLabels, kindLabels],
  );
  const [configs, setConfigs] = useState<admin.ConfigVersionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadConfigs = useCallback(() => {
    setLoading(true);
    setError(null);
    api.admin
      .listConfigVersions()
      .then((res) => setConfigs(res.data))
      .catch((err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err !== null && 'message' in err
              ? String((err as { message: unknown }).message)
              : t('configPromotions.errors.loadFailed');
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState message={error} onRetry={loadConfigs} />;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-ink">{t('configPromotions.headings.title')}</h1>
      </div>
      <DataTable
        columns={columns}
        data={configs}
        rowKey={(c) => c.id}
        emptyMessage={t('configPromotions.empty')}
        ariaLabel={t('configPromotions.ariaLabel')}
      />
    </div>
  );
}
